/**
 * SubscriptionChannelListingCtrl controller
 */

import { compose } from 'app/helpers/fn'
import { property } from 'app/helpers/object'

import { CHANNEL_LISTING_TYPES } from 'app/core/constants'

// preload drag'n'drop image
const dragndropImg = new window.Image()
dragndropImg.src = require('./img/dragndrop.png')

// check if string has (and hasn't) substring, case-insensitive
const has = (a) => (b) => b.toLowerCase().indexOf(a.toLowerCase()) > -1

/**
 * SubscriptionChannelListingCtrl controller
 *
 * @class SubscriptionChannelListingCtrl
 * @memberof mwApp/packages
 * @see mwApp/packages
 * @mixes mwApp/common.mixin:tabsCtrlMixin
 *
 * @ngdoc controller
 * @name mwApp/packages.controller:SubscriptionChannelListingCtrl
 */
/* @ngInject */
export default function SubscriptionChannelListingCtrl ($scope, $uibModal, SubscriptionChannelListing, ChannelGroup, cl, subscription, ungroupedChannels, alertSvc) {
  var vm = this
  vm.cl = cl
  vm.ungroupedChannels = ungroupedChannels
  vm.subscription = subscription

  /**
   * Channel listing
   *
   * @memberof mwApp/packages.controller:SubscriptionChannelListingCtrl
   * @type {String}
   */
  vm.listings = CHANNEL_LISTING_TYPES

  vm.filter = {
    sequence: '',
    group: '',
    ungrouped: ''
  }

  if (!vm.cl.channels.length) {
    SubscriptionChannelListing.getChannelList(vm.cl).then(res => {
      vm.cl.channels = res
    })
  }

  /**
   * Open/Close overlays, if they has/hasn't searched channels
   *
   * @memberof mwApp/packages.controller:SubscriptionChannelListingCtrl
   * @param {String} filter
   */
  vm.filterGroups = function (filter) {
    const byHas = compose(
      has(filter),
      property('name')
    )

    vm.cl.groups.forEach(overlay => {
      overlay.is_open = !filter
        ? false
        : overlay.channels.filter(byHas).length
    })
  }

  /**
   * In the dnd-drop callback, we now have to handle the data array that we
   * sent above. We handle the insertion into the list ourselves. By returning
   * true, the dnd-list directive won't do the insertion itself.
   */
  vm.onDrop = function (list, items, index, to = '') {
    $scope.form.$setDirty()
    items.forEach(item => { item.selected = false })
    if (vm.cl.listingType === 'SEQUENCE') {
      vm.cl.channels = vm.cl.channels
        .slice(0, index)
        .concat(items)
        .concat(list.slice(index))
    } else {
      if (to === 'ungrouped') {
        vm.ungroupedChannels = vm.ungroupedChannels
          .slice(0, index)
          .concat(items)
          .concat(vm.ungroupedChannels.slice(index))
      } else {
        if (!vm.localDnd && list.channels
          .slice(0, index)
          .concat(items)
          .concat(list.channels.slice(index)).length > (list.toNumber - list.fromNumber + 1)) {
          return false
        }
        list.channels = list.channels
          .slice(0, index)
          .concat(items)
          .concat(list.channels.slice(index))
      }
    }
    return true
  }

  /**
   * dnd-dragging determines what data gets serialized and send to the receiver
   * of the drop. While we usually just send a single object, we send the array
   * of all selected items here.
   */
  vm.getSelected = function (list, link, local) {
    vm.localDnd = local

    link.selected = true
    return list.filter(link => link.selected)
  }

  /**
   * We set the list into dragging state, meaning the items that are being
   * dragged are hidden. We also use the HTML5 API directly to set a custom
   * image, since otherwise only the one item that the user actually dragged
   * would be shown as drag image.
   */
  vm.onDragstart = function (list, event) {
    list.dragging = true
    if (event.dataTransfer.setDragImage) {
      event.dataTransfer.setDragImage(dragndropImg, 0, 0)
    }
  }

  /**
   * We set the list into dragging state, meaning the items that are being
   * dragged are hidden. We also use the HTML5 API directly to set a custom
   * image, since otherwise only the one item that the user actually dragged
   * would be shown as drag image.
   */
  vm.onDragend = function (list) {
    vm.localDnd = false
    list.dragging = false
  }

  /**
   * Last but not least, we have to remove the previously dragged items in the
   * dnd-moved callback.
   */
  vm.onMoved = function (list, from = '') {
    if (vm.cl.listingType === 'SEQUENCE') {
      vm.cl.channels = vm.cl.channels.filter(item => !item.selected)
    } else {
      if (from === 'group') {
        list.channels = list.channels.filter(item => !item.selected)
      }
      vm.ungroupedChannels = vm.ungroupedChannels.filter(item => !item.selected)
    }
  }

  /* === update, delete, move... === */

  /**
   * Update network
   *
   * @memberof mwApp/packages.controller:SubscriptionChannelListingCtrl
   */
  vm.save = function () {
    const groupsValidator = () => vm.cl.groups.map(group => group?.channels.length).some(groupChannelsLength => groupChannelsLength === 0)

    if (vm.cl.listingType === 'GROUP' && groupsValidator()) {
      alertSvc.alert('One of channels group has no assigned TV Channel(s)', 'warning')
      return
    }

    if (vm.cl.listingType === 'GROUP' && vm.cl.groups.some(group => {
      if (group.toNumber - group.fromNumber + 1 < group.channels.length) {
        alertSvc.alert(`Only ${group.toNumber - group.fromNumber + 1} channels can be assigned to group "${group.description}"`, 'warning')
        return true
      }
      return false
    })) {
      return
    }

    SubscriptionChannelListing.save(vm.cl)
      .then(() => {
        $scope.form.$setPristine()
      })
  }

  /**
   * Open add/edit Overlay modal window
   *
   * @memberof mwApp/packages.controller:SubscriptionChannelListingCtrl
   */
  vm.open = function (channelGroup) {
    var isNew = !channelGroup
    var initialOverlay = angular.copy(channelGroup) // initial state
    $uibModal.open({
      animation: false,
      templateUrl: 'components/packages/views/addChannelGroup.html',
      controller: 'AddChannelGroupCtrl as vm',
      size: 'lg',
      resolve: {
        overlay: () => isNew ? new ChannelGroup() : channelGroup,
        cl: () => vm.cl,
        isNew: () => isNew
      }
    }).result
      .then(
        function close (channelGroup) {
          $scope.form.$setDirty()
          if (isNew) {
            vm.cl.groups.push(channelGroup)
          }
        },
        function dismiss () {
          if (!isNew) {
            angular.copy(initialOverlay, channelGroup) // restore
          }
        }
      )
  }

  /**
   * Remove overlay from Network overlays
   *
   * @memberof mwApp/packages.controller:SubscriptionChannelListingCtrl
   */
  vm.delete = function (fromNumber) {
    const index = vm.cl.groups.findIndex(group => group.fromNumber === fromNumber)
    const group = vm.cl.groups.splice(index, 1)[0]
    vm.ungroupedChannels.push(...group.channels)
  }

  vm.disableGroupDndAssign = function (overlay) {
    const selectedChannelsLength = vm.ungroupedChannels.filter(chanel => chanel.selected).length
    const groupLength = overlay.toNumber - overlay.fromNumber + 1
    const condition = !vm.localDnd && overlay.channels.length + selectedChannelsLength > groupLength
    if (condition) {
      alertSvc.alert(`Only ${groupLength} channels can be assigned to group`, 'warning')
    }
    return condition
  }
}
